import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Alert, Button, DatePicker, Form, InputNumber, Radio } from 'antd'
import dayjs from 'dayjs'
import { serviceChargeInputSchema, serviceTermSchema, type ServiceTermInput } from '@super-mega/server/contracts/order'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmount } from '@/utils/money.ts'
import { ServiceTermRuleFields } from './service-term-rule-fields.tsx'

export function defaultServiceTerm(): ServiceTermInput {
  return {
    startAt: dayjs().format('YYYY-MM-DD'),
    endAt: dayjs().add(1, 'year').subtract(1, 'day').format('YYYY-MM-DD'),
    rules: [
      serviceChargeInputSchema.parse({
        feeName: '服务费',
        unitPrice: 0,
        billingDay: 1,
        alignment: 'anniversary',
        dueOffsetDays: 0
      })
    ]
  }
}

export function ServicePricingFields({
  name = [],
  watchPrefix = [],
  disabled
}: {
  name?: (string | number)[]
  watchPrefix?: (string | number)[]
  disabled: boolean
}) {
  const form = Form.useFormInstance()
  const term = Form.useWatch<ServiceTermInput | null>([...watchPrefix, 'term'], { form, preserve: true })
  return (
    <div className="col-span-full">
      <Form.Item label="服务模式">
        <Radio.Group
          aria-label="服务模式"
          disabled={disabled}
          value={term ? 'term' : 'once'}
          options={[
            { label: '一次性服务', value: 'once' },
            { label: '期限服务', value: 'term' }
          ]}
          onChange={(event) => {
            form.setFieldValue([...watchPrefix, 'term'], event.target.value === 'term' ? defaultServiceTerm() : null)
            form.setFieldValue([...watchPrefix, 'agreedAmount'], undefined)
          }}
        />
      </Form.Item>
      {term ? (
        <Form.Item
          name={[...name, 'term']}
          rules={[
            {
              validator: async (_, value) => {
                const result = serviceTermSchema.safeParse(value)
                if (!result.success) throw new Error(result.error.issues[0]?.message ?? '请完善期限与费用')
              }
            }
          ]}
        >
          <ServiceTermEditor disabled={disabled} />
        </Form.Item>
      ) : (
        <Form.Item
          label="应收金额"
          name={[...name, 'agreedAmount']}
          rules={[{ required: true, type: 'number', min: 0, message: '请输入应收金额' }]}
        >
          <InputNumber disabled={disabled} className="w-full" min={0} precision={2} prefix="¥" />
        </Form.Item>
      )}
    </div>
  )
}

export function ServiceTermEditor({
  value,
  onChange,
  disabled = false
}: {
  value?: ServiceTermInput
  onChange?: (value: ServiceTermInput) => void
  disabled?: boolean
}) {
  const trpc = useTRPC()
  const client = useQueryClient()
  const [preview, setPreview] = useState<{ input: string; lines: string[] }>()
  const [error, setError] = useState<string>()
  const [loading, setLoading] = useState(false)
  if (!value) return null
  const change = (patch: Partial<ServiceTermInput>) => onChange?.({ ...value, ...patch })
  const updateRule = (index: number, patch: Partial<ServiceTermInput['rules'][number]>) => {
    change({ rules: value.rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)) })
  }
  const showPreview = async () => {
    const parsed = serviceTermSchema.safeParse(value)
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message)
      return
    }
    setLoading(true)
    setError(undefined)
    try {
      const result = await client.fetchQuery(trpc.order.service.previewTerm.queryOptions(parsed.data))
      setPreview({
        input: JSON.stringify(value),
        lines: result.flatMap((rule) =>
          rule.periods
            .slice(0, 6)
            .map(
              (p) =>
                `${rule.feeName}：${p.periodStart} 至 ${p.periodEnd}，¥${formatAmount(p.amount)}，计划 ${p.scheduledAt} 出账，${p.dueAt} 到期`
            )
        )
      })
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '预览失败')
    } finally {
      setLoading(false)
    }
  }
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
        <Form.Item label="开始服务日" required>
          <DatePicker
            aria-label="开始服务日"
            disabled={disabled}
            className="w-full"
            value={dayjs(value.startAt)}
            allowClear={false}
            onChange={(date) => {
              if (date) change({ startAt: date.format('YYYY-MM-DD') })
            }}
          />
        </Form.Item>
        <Form.Item label="最后服务日（含当天）" required>
          <DatePicker
            aria-label="最后服务日"
            disabled={disabled}
            className="w-full"
            value={dayjs(value.endAt)}
            allowClear={false}
            onChange={(date) => {
              if (date) change({ endAt: date.format('YYYY-MM-DD') })
            }}
          />
        </Form.Item>
      </div>
      {value.rules.map((rule, index) => (
        <div key={index} className="border-border rounded-lg border p-4">
          <ServiceTermRuleFields rule={rule} disabled={disabled} onChange={(patch) => updateRule(index, patch)} />
          <Authorized permissions={['order.service.delete']}>
            <Button
              danger
              disabled={disabled || value.rules.length <= 1}
              onClick={() => change({ rules: value.rules.filter((_, i) => i !== index) })}
            >
              删除费用
            </Button>
          </Authorized>
        </div>
      ))}
      <div className="flex flex-wrap gap-3">
        <Button
          disabled={disabled || value.rules.length >= 20}
          onClick={() =>
            change({
              rules: [
                ...value.rules,
                serviceChargeInputSchema.parse({
                  feeName: '服务费',
                  unitPrice: 0,
                  billingDay: 1,
                  alignment: 'anniversary',
                  dueOffsetDays: 0
                })
              ]
            })
          }
        >
          添加费用
        </Button>
        <Button loading={loading} onClick={() => void showPreview()}>
          预览各项前六期账单
        </Button>
      </div>
      {error && <Alert type="error" title={error} />}
      {preview?.input === JSON.stringify(value) && (
        <Alert
          type="info"
          title="账单预览"
          description={
            <ul>
              {preview.lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          }
        />
      )}
      <p className="text-text-secondary text-sm">
        月周期支持 1、3、6、12
        个月。创建时生成首期草稿，后续账期开通后按计划生成，正式出账和收款分别处理；押金独立核算。
      </p>
    </div>
  )
}
